import { clsx } from 'clsx/lite'
import { CircleCheck, LoaderCircle, RefreshCw, X } from 'lucide-react'
import { Button } from '../components/Button'
import { IconButton } from '../components/IconButton'
import { useOverflowEdges } from '../components/overflow-edges'
import { MESSAGES } from '../messages'
import { useRef, type ReactNode } from 'react'
import type { FindingKey } from '../state/store'
import { COLOR_CLASSES } from '../theme/roles'
import { SEVERITY_NAMES, type HealthRow, type MissingReport } from './health'
import { SeverityIcon } from './SeverityIcon'

export type HealthListProps = {
  /** The name of the profile the findings were checked against. */
  readonly profileName: string
  /** The findings to list, in display order (`healthRows`). */
  readonly rows: readonly HealthRow[]
  /** Whether the document has a body the analysis checks; without one the panel asks for a body. */
  readonly hasGeometry: boolean
  /** Why there is no report to list yet (`missingReport`); absent or null when there is one. */
  readonly missingReport?: MissingReport | null
  readonly onCheckAgain: () => void
  /** Frames a finding in the viewport and marks it (`focusFinding`). */
  readonly onFocusFinding: (finding: FindingKey) => void
  /** The finding the viewport marks, highlighted in the list. */
  readonly focusedFinding?: FindingKey | null
  /** Closes the panel; without it the panel has no close button. */
  readonly onClose?: () => void
  /** The id of the panel's heading, so the region around the panel can be labeled by it. */
  readonly headingId?: string
  /** The body whose picked face rests on the bed (Lay on face, 3.17); absent or null prints as modeled. */
  readonly orientationBody?: string | null
  /** Prints as modeled again; the orientation line's "Print as modeled". */
  readonly onPrintAsModeled?: () => void
  /** Starts the Lay on face pick; without it the rows offer no "Lay on a face". */
  readonly onLayOnFace?: () => void
}

/** Checks whose findings another face on the bed can fix, so their rows offer "Lay on a face" (3.17). */
const LAY_ON_FACE_CHECKS: ReadonlySet<string> = new Set([
  'overhangs',
  'first_layer_contact',
  'islands',
])

function sameFinding(first: FindingKey, second: FindingKey | null | undefined): boolean {
  return (
    second !== null &&
    second !== undefined &&
    first.body === second.body &&
    first.check === second.check &&
    first.index === second.index
  )
}

/**
 * A row's accessible name: "{severity}: {message} {body}", with the suggestion's words before the
 * body name when there are any (3.17).
 */
function rowName(row: HealthRow): string {
  const suggestion = row.suggestion === null ? '' : ` ${row.suggestion}.`
  return `${SEVERITY_NAMES[row.severity]}: ${row.message}${suggestion} ${row.bodyName}`
}

/** What the panel says instead of listing findings, or null when it lists them. */
function noticeFor(
  hasGeometry: boolean,
  missingReport: MissingReport | null,
  rowCount: number,
  profileName: string,
): ReactNode {
  if (!hasGeometry) {
    return (
      <p className={clsx('p-4 text-sm', COLOR_CLASSES.textMuted.foreground)}>
        Add a body to check it for printing.
      </p>
    )
  }
  if (missingReport === 'checking') {
    return (
      <p className={clsx('flex items-start gap-2 p-4 text-sm', COLOR_CLASSES.textMuted.foreground)}>
        <LoaderCircle
          aria-hidden="true"
          strokeWidth={1.75}
          className="mt-0.5 size-4 shrink-0 animate-spin motion-reduce:animate-none"
        />
        <span>Checking the model for {profileName}.</span>
      </p>
    )
  }
  if (missingReport === 'unchecked') {
    return (
      <p className={clsx('p-4 text-sm', COLOR_CLASSES.textMuted.foreground)}>Not checked yet.</p>
    )
  }
  if (rowCount === 0) {
    return (
      <p className="flex items-start gap-2 p-4 text-sm">
        <CircleCheck
          aria-hidden="true"
          strokeWidth={1.75}
          className={clsx('mt-0.5 size-4 shrink-0', COLOR_CLASSES.successMark.foreground)}
        />
        <span>No print issues found with {profileName}.</span>
      </p>
    )
  }
  return null
}

/**
 * The print check panel (`health-list`, ui.md 3.17): "Print check: {profile name}", "Check
 * again", and one row per finding with its suggestion's words. Clicking a row, or Enter on it,
 * frames the finding in the viewport.
 */
export function HealthList({
  profileName,
  rows,
  hasGeometry,
  missingReport = null,
  onCheckAgain,
  onFocusFinding,
  focusedFinding = null,
  onClose,
  headingId,
  orientationBody = null,
  onPrintAsModeled,
  onLayOnFace,
}: HealthListProps) {
  const notice = noticeFor(hasGeometry, missingReport, rows.length, profileName)
  return (
    <div data-testid="health-list" className="flex h-full min-h-0 flex-col">
      <div className="flex flex-col gap-3 p-4">
        <div className="flex min-h-6 items-start justify-between gap-2">
          <h2 id={headingId} className="min-w-0 text-base font-semibold break-words">
            Print check: {profileName}
          </h2>
          {onClose === undefined ? null : (
            <IconButton size="compact" label="Close print check" icon={X} onClick={onClose} />
          )}
        </div>
        <Button className="self-start" onClick={onCheckAgain}>
          <RefreshCw aria-hidden="true" strokeWidth={1.75} className="size-4" />
          Check again
        </Button>
        {orientationBody === null ? null : (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <p className="min-w-0">{MESSAGES.orientation_set(orientationBody)}</p>
            {onPrintAsModeled === undefined ? null : (
              <Button onClick={onPrintAsModeled}>Print as modeled</Button>
            )}
          </div>
        )}
      </div>
      {notice ?? (
        <FindingsList
          rows={rows}
          focusedFinding={focusedFinding}
          onFocusFinding={onFocusFinding}
          onLayOnFace={onLayOnFace}
        />
      )}
    </div>
  )
}

type FindingsListProps = {
  readonly rows: readonly HealthRow[]
  readonly focusedFinding: FindingKey | null
  readonly onFocusFinding: (finding: FindingKey) => void
  /** Starts the Lay on face pick; undefined offers no "Lay on a face". */
  readonly onLayOnFace: (() => void) | undefined
}

/** The findings, one row each, scrolling under the panel's header block (3.17). */
function FindingsList({ rows, focusedFinding, onFocusFinding, onLayOnFace }: FindingsListProps) {
  const list = useRef<HTMLUListElement>(null)
  useOverflowEdges(list)
  return (
    <ul
      ref={list}
      aria-label="Findings"
      // Its rule shows while findings are scrolled under the header block (3.2).
      className={clsx(
        'flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto border-t p-2',
        COLOR_CLASSES.forcedEdge.border,
        COLOR_CLASSES.divider.overflowTop,
      )}
    >
      {rows.map((row) => {
        const focused = sameFinding(row.key, focusedFinding)
        // The finding text is the button that focuses the finding, so the row's other button
        // never nests in it.
        const layOnFace =
          onLayOnFace !== undefined && LAY_ON_FACE_CHECKS.has(row.key.check) ? onLayOnFace : null
        return (
          <li
            key={`${row.key.body}:${row.key.check}:${String(row.key.index)}`}
            className="flex items-start gap-1"
          >
            <button
              type="button"
              data-testid="health-item"
              data-check={row.key.check}
              data-severity={row.severity}
              aria-current={focused ? 'true' : undefined}
              aria-label={rowName(row)}
              onClick={() => {
                onFocusFinding(row.key)
              }}
              className={clsx(
                'flex min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors duration-150 motion-reduce:transition-none',
                COLOR_CLASSES.text.foreground,
                COLOR_CLASSES.focusRing.outline,
                // The transparent state outline, at rest too, so transition-colors never fades a
                // colored outline in or out; forced colors draw it on the focused finding (3.23).
                COLOR_CLASSES.forcedEdge.outline,
                focused && 'outline -outline-offset-1',
                focused ? COLOR_CLASSES.selection.background : COLOR_CLASSES.hover.background,
              )}
            >
              <span className="flex h-5 shrink-0 items-center">
                <SeverityIcon severity={row.severity} />
              </span>
              <span className="flex min-w-0 flex-col gap-0.5">
                <span>{row.message}</span>
                {row.suggestion === null ? null : (
                  <span data-testid="health-suggestion">{row.suggestion}</span>
                )}
                <span className={clsx('text-xs', COLOR_CLASSES.textMuted.foreground)}>
                  {row.bodyName}
                </span>
              </span>
            </button>
            {layOnFace === null ? null : (
              // 1rem from the panel's edge, in line with the header's close button.
              <Button className="mt-1 mr-2" onClick={layOnFace}>
                Lay on a face
              </Button>
            )}
          </li>
        )
      })}
    </ul>
  )
}
